/**
 * Specifies the type of storage to be used: either local storage or session storage.
 */
export type StorageType = 'local' | 'session';

/**
 * Stores a value with the specified key in the selected storage.
 *
 * @param key - The key under which the value will be stored.
 * @param value - The value to be stored.
 * @param storageType - The type of storage to use: local or session. Default is local storage.
 */
const setStorageItem = (
  key: string,
  value: any,
  storageType: StorageType = 'local'
): void => {
  const storage = storageType === 'local' ? localStorage : sessionStorage;
  storage.setItem(key, JSON.stringify(value));
};

/**
 * Retrieves the value associated with the specified key from the selected storage.
 *
 * @param key - The key of the value to retrieve.
 * @param storageType - The type of storage to use: local or session. Default is local storage.
 * @returns The retrieved value, or null if the key is not found.
 */
const getStorageItem = (
  key: string,
  storageType: StorageType = 'local'
): any | null => {
  const storage = storageType === 'local' ? localStorage : sessionStorage;
  const item = storage.getItem(key);
  return item ? JSON.parse(item) : null;
};

/**
 * Removes the value associated with the specified key from the selected storage.
 *
 * @param key - The key of the value to remove.
 * @param storageType - The type of storage to use: local or session. Default is local storage.
 */
const removeStorageItem = (
  key: string,
  storageType: StorageType = 'local'
): void => {
  const storage = storageType === 'local' ? localStorage : sessionStorage;
  storage.removeItem(key);
};

export const clearLegacyStorage = (
  storage: Storage | undefined,
  keys: readonly string[],
  prefixes: readonly string[] = []
) => {
  if (!storage) return;

  keys.forEach((key) => storage.removeItem(key));

  const matchingKeys: string[] = [];
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
      matchingKeys.push(key);
    }
  }
  matchingKeys.forEach((key) => storage.removeItem(key));
};

export const createUserScopedStorage = (
  storage: Storage | undefined,
  namespace: string
) => {
  let userId: string | null = null;

  const getPrefix = () =>
    userId ? `${namespace}:user:${encodeURIComponent(userId)}:` : null;

  const scopedStorage: Storage = {
    get length() {
      const prefix = getPrefix();
      if (!prefix || !storage) return 0;

      let count = 0;
      for (let index = 0; index < storage.length; index++) {
        if (storage.key(index)?.startsWith(prefix)) count++;
      }
      return count;
    },
    clear() {
      const prefix = getPrefix();
      if (!prefix) return;
      clearLegacyStorage(storage, [], [prefix]);
    },
    getItem(key: string) {
      const prefix = getPrefix();
      return prefix && storage ? storage.getItem(`${prefix}${key}`) : null;
    },
    key(index: number) {
      const prefix = getPrefix();
      if (!prefix || !storage) return null;

      const keys: string[] = [];
      for (
        let storageIndex = 0;
        storageIndex < storage.length;
        storageIndex++
      ) {
        const key = storage.key(storageIndex);
        if (key?.startsWith(prefix)) keys.push(key.slice(prefix.length));
      }
      return keys[index] ?? null;
    },
    removeItem(key: string) {
      const prefix = getPrefix();
      if (prefix) storage?.removeItem(`${prefix}${key}`);
    },
    setItem(key: string, value: string) {
      const prefix = getPrefix();
      if (prefix) storage?.setItem(`${prefix}${key}`, value);
    },
  };

  return {
    getUserId: () => userId,
    setUserId: (nextUserId: string | null) => {
      userId = nextUserId;
    },
    storage: scopedStorage,
  };
};

export { getStorageItem, removeStorageItem, setStorageItem };
